import {useRef, useState} from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Selector} from 'notebook/ELNConstants';
import DocumentEvaluationUpdater from 'notebook/mediocre/DocumentEvaluationUpdater';
import {DocumentMapper, DocumentTraverser} from 'notebook/mediocre/DocumentTraversal';
import {Table} from 'notebook/tables/constants/TableConstants';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {
  TableLocationNameArgs,
  FullTableMap,
  OutputTableMap,
} from 'notebook/tables/excess/EvaluationConstants';
import * as SpreadsheetEvalUtil from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import spreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';
import {createNoteRuntimeSnapshot, NoteRuntimeSnapshot} from 'notebook/tables/TableStoreTransforms';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import APIUtil from 'util/APIUtil';
import uniqueId from 'util/uniqueId';

import {EnhancedProps} from './SimpleTableEditable';

export interface SimpleTableDoc {
  cid: string;
  noteRuntimeSnapshot: NoteRuntimeSnapshot;
  fullTableMap: FullTableMap;
  selectors: Array<Selector>;
}

export type EvaluatedSimpleTableDoc = SimpleTableDoc & {
  noteRuntimeSnapshot: NoteRuntimeSnapshot & {evaluationSnapshot: OutputTableMap};
};

export function isEvaluated(doc: SimpleTableDoc): doc is EvaluatedSimpleTableDoc {
  return doc.noteRuntimeSnapshot.evaluationSnapshot != null;
}

const EMPTY_SELECTORS: Array<Selector> = [];

/**
 * A React-free abstraction for doing evaluation of tables that notably produces NoteRuntimeSnapshot without
 * being coupled to the object types provided by Notebook Canvas (entry, protocol, notes).
 *
 * Limitation: It requires you to transform your object into a SimpleTableDoc. This is the result of
 * DocumentTraverser / DocumentMapper currently being coupled to a fixed set of object types.
 *
 * See {@link useSimpleEvaluation} implementation as an example of how you can construct a SimpleTableDoc.
 *
 * This makes it useful for doing evaluation that is intended to be persisted or if the object state is being
 * managed outside of React (e.g. a FluxStore).
 */
export class SimpleTableDocUpdater extends DocumentEvaluationUpdater<SimpleTableDoc, SimpleTableDoc> {
  traverse(evalTraverser: DocumentTraverser, newDoc: SimpleTableDoc, oldDoc: SimpleTableDoc): void {
    // Ideally DocumentTraverser has a simpler interface for triggering evaluation intead
    // of being intimately aware of the shape of SimpleTableDoc.
    evalTraverser.walkSimpleTableDoc(newDoc, oldDoc);
  }

  mapDoc(mapper: DocumentMapper, doc: SimpleTableDoc): SimpleTableDoc {
    // Ideally DocumentMapper has a simpler interface for returning back the new evaluated table
    // and letting this mapDoc implementation handle instead of being intimately aware of the shape
    // of SimpleTableDoc.
    return mapper.mapSimpleTableDoc(doc, null);
  }

  getDoc(root: SimpleTableDoc): SimpleTableDoc {
    return root;
  }

  updatedRoot(root: SimpleTableDoc, doc: SimpleTableDoc): SimpleTableDoc {
    return doc;
  }
}

export function getInitialDoc(
  fullTableMap: FullTableMap,
  selectors: Array<Selector> = EMPTY_SELECTORS
): SimpleTableDoc {
  return {
    cid: APIUtil.prefixApiId(ModelTypeConstants.SIMPLE_TABLE_DOC, uniqueId()),
    noteRuntimeSnapshot: createNoteRuntimeSnapshot(),
    fullTableMap,
    selectors,
  };
}

/**
 * Hook to evaluate a set of tables.
 *
 * Uses a DocumentEvaluationUpdater under the hood.
 *
 * @example
 * function MyTable() {
 *   const [fullTableMap, setFullTableMap] = useState({
 *     'Sheet1': {
 *       name: 'Sheet1',
 *       widths: [100, 100],
 *       columnConstraints: [null, null],
 *       columnLabels: [null, null],
 *       table: [[{text: '1'}, {text: '2'}]],
 *       headerRow: false,
 *     },
 *   });
 *   const simpleTableDoc = useSimpleEvaluation(fullTableMap);
 *   const props = useSimpleUI('Sheet1', simpleTableDoc)
 *   return (
 *     <SimpleTableEditable
 *        {...standaloneProps}
 *        {...props}
 *        cid="sheet1"
 *        storeState={noop}
 *        locked={false}
 *        updateHandler={(_, {table}) => {
 *          setFullTableMap({...fullTableMap, Sheet1: table});
 *        }}
 *     />
 *   );
 * }
 */
export default function useSimpleEvaluation(
  fullTableMap: FullTableMap,
  selectors: Array<Selector> = EMPTY_SELECTORS
): EvaluatedSimpleTableDoc {
  // Because we are using a ref to track the live document, we use this a mechanism to trigger a re-render.
  const [, setForceRender] = useState(0);

  // A live SimpleTableDoc. This is needed so that async updates triggered from SimpleTableDocUpdater
  // merges resoutions into the latest version of the document.
  const liveDocRef = useRef<SimpleTableDoc>(getInitialDoc(fullTableMap, selectors));

  // The version of the document that was last evaluated.
  const prevEvalDoc = useRef<SimpleTableDoc | null>(null);

  // Re-evaluate if:
  if (
    // A. We have not yet done an evaluation (first render)
    prevEvalDoc == null ||
    // B. The subcomponents of the document that affect evaluation have changed
    liveDocRef.current.fullTableMap !== fullTableMap ||
    liveDocRef.current.selectors !== selectors ||
    // C. Or the snapshot has changed (e.g. query resolutions have resolved)
    liveDocRef.current !== prevEvalDoc.current
  ) {
    const updater = new SimpleTableDocUpdater(
      liveDocRef.current.cid,
      () => liveDocRef.current,
      (doc) => {
        // These are asynchronous updates.
        liveDocRef.current = doc;
        // Because the hook is returning a field on a ref, we need to trigger a re-render.
        setForceRender((count) => count + 1);
      }
    );
    // Merge in any changes (B)
    liveDocRef.current = {...liveDocRef.current, fullTableMap, selectors};
    // And then (re-)evaluate.
    liveDocRef.current = updater.run(liveDocRef.current, prevEvalDoc.current);
    prevEvalDoc.current = liveDocRef.current;
  }
  const rv = liveDocRef.current;
  assert(isEvaluated(rv), 'Expected SimpleTableDocUpdater to produce an evaluationSnapshot');
  return rv;
}

/**
 * Hook to connect a SimpleTableEditable to the SpreadsheetStore.
 *
 * See {@link useSimpleEvaluation} for example usage.
 *
 * @returns null if the table is not found in the SimpleTableDoc.
 */
export function useSimpleUI(
  tableName: string,
  simpleTableDoc: EvaluatedSimpleTableDoc,
  hasFormulasDisabled: boolean = false
): (EnhancedProps & {table: Table}) | null {
  const tableNamespace = `ns_${simpleTableDoc.cid}`;
  const {cellColors, editingTable, editingDocumentName} = useStoreSelector(spreadsheetStore, () =>
    SpreadsheetEvalUtil.getSpreadsheetStoreState(tableNamespace, hasFormulasDisabled)
  );
  if (simpleTableDoc.fullTableMap[tableName] == null) {
    return null;
  }
  const table = simpleTableDoc.fullTableMap[tableName];
  const computedTable = simpleTableDoc.noteRuntimeSnapshot.evaluationSnapshot[tableName];
  const props: EnhancedProps & {table: Table} = {
    table,
    cellColors,
    computedTables: {[table.name]: computedTable},
    editingTable,
    setMultiEditingForTable(
      {tableName, documentName}: TableLocationNameArgs,
      {
        index,
        editing,
        text,
        typedValue,
        selection,
      }: {
        index: TableIndex | null;
        editing: boolean;
        text: string;
        typedValue: TypedValue;
        selection: {cid: string; start: number; end: number} | null;
      }
    ): void {
      SpreadsheetEvalUtil.setMultiEditingForTable({tableName, documentName}, tableNamespace, null, {
        editing,
        index,
        text,
        typedValue,
        selection,
      });
    },
    tableNamespace,
    canInsert(e: {start: number; end: number; cid: string; shift: boolean}): boolean {
      return SpreadsheetEvalUtil.canInsert({
        e,
        hasFormulasDisabled,
        tableNamespace,
        editingTable,
        editingDocumentName,
      });
    },
    tryInsert(
      {tableName, documentName}: TableLocationNameArgs,
      e: {index: [number, number]; start: number; end: number; cid: string; shift: boolean}
    ): boolean {
      return SpreadsheetEvalUtil.tryInsert({
        tableName,
        documentName,
        e,
        hasFormulasDisabled,
        tableNamespace,
      });
    },
  };
  return props;
}
